import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  HiOutlineBriefcase, 
  HiOutlinePencil, 
  HiOutlineTrash, 
  HiOutlineArrowLeft,
  HiOutlineUser,
  HiOutlineDocumentText,
  HiOutlineCalendar,
  HiOutlineExclamationCircle,
  HiOutlineEye,
  HiOutlineLocationMarker,
  HiOutlineGlobeAlt,
  // HiOutlineMail,
  // HiOutlinePhone
} from 'react-icons/hi';
import { Application, Company, Contact, Resume } from '../types';
import { applicationsService } from '../services/applicationsService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Format date to "8th May 2025" format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  const statusOptions = [
    { value: 'applied', label: 'Applied', color: 'bg-blue-100 text-blue-800' },
    { value: 'under_review', label: 'Under Review', color: 'bg-purple-100 text-purple-800' },
    { value: 'phone_screening', label: 'Phone Screening', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'technical_test', label: 'Technical Test', color: 'bg-orange-100 text-orange-800' },
    { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'interviewed', label: 'Interviewed', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'final_round', label: 'Final Round', color: 'bg-teal-100 text-teal-800' },
    { value: 'offer_received', label: 'Offer Received', color: 'bg-green-100 text-green-800' },
    { value: 'offer_accepted', label: 'Offer Accepted', color: 'bg-green-200 text-green-900' },
    { value: 'offer_declined', label: 'Offer Declined', color: 'bg-gray-100 text-gray-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'withdrawn', label: 'Withdrawn', color: 'bg-gray-100 text-gray-800' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'High', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const sourceOptions = [
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'indeed', label: 'Indeed' },
    { value: 'glassdoor', label: 'Glassdoor' },
    { value: 'company_website', label: 'Company Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'recruiter', label: 'Recruiter' },
    { value: 'cold_email', label: 'Cold Email' },
    { value: 'other', label: 'Other' }
  ];

  const fetchApplicationData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const applicationRes = await applicationsService.getApplication(id);
      setApplication(applicationRes);
    } catch (error) {
      console.error('Error fetching application data:', error);
      toast.error('Failed to load application data');
      navigate('/applications');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchApplicationData();
    }
  }, [id, fetchApplicationData]);

  const handleEdit = () => {
    // Navigate to applications page with edit mode
    navigate('/applications', { state: { editApplication: application } });
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await applicationsService.deleteApplication(id);
      toast.success('Application deleted successfully');
      navigate('/applications');
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Failed to delete application');
    }
  };

  const getStatusColor = (status: string): string => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string): string => {
    const priorityOption = priorityOptions.find(p => p.value === priority);
    return priorityOption ? priorityOption.color : 'bg-gray-100 text-gray-800';
  };

  const getCompanyName = (company: string | Company): string => {
    return typeof company === 'string' ? 'Unknown Company' : company?.name || 'Unknown Company';
  };

  const getResumeName = (resume: string | Resume | undefined): string => {
    if (!resume) return 'No resume';
    return typeof resume === 'string' ? 'Resume' : resume?.title || resume?.originalName || 'Resume';
  };

  const getContactName = (contact: string | Contact | undefined): string => {
    if (!contact) return 'No contact';
    return typeof contact === 'string' ? 'Contact' : contact?.name || 'Contact';
  };

  const getContactId = (contact: string | Contact | undefined): string | null => {
    if (!contact) return null;
    return typeof contact === 'string' ? contact : contact._id;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <HiOutlineBriefcase className="mx-auto h-12 w-12 text-secondary-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">Application not found</h3>
        <p className="mt-1 text-sm text-secondary-500">
          The application you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate('/applications')}
          className="mt-4 btn btn-primary"
        >
          Back to Applications
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/applications')}
            className="p-2 text-secondary-400 hover:text-secondary-600 rounded-full hover:bg-secondary-100"
          >
            <HiOutlineArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{application.role}</h1>
            <p className="mt-1 text-sm text-secondary-600">
              at {getCompanyName(application.company)}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleEdit}
            className="btn btn-secondary"
          >
            <HiOutlinePencil className="h-4 w-4 mr-2" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
          >
            <HiOutlineTrash className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Application Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status and Priority */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-secondary-900">Application Status</h3>
            </div>
            <div className="card-content">
              <div className="flex items-center gap-4 mb-6">
                <div>
                  <h4 className="text-sm font-medium text-secondary-500 mb-2">Current Status</h4>
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                    {statusOptions.find(s => s.value === application.status)?.label || application.status}
                  </span>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-secondary-500 mb-2">Priority</h4>
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getPriorityColor(application.priority || 'medium')}`}>
                    {priorityOptions.find(p => p.value === application.priority)?.label || application.priority}
                  </span>
                </div>
              </div>

              {/* Special Status Indicators */}
              {(application.status === 'offer_received' || application.status === 'offer_accepted') && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <span className="text-lg">🎉</span>
                    <span className="font-medium">
                      {application.status === 'offer_received' ? 'Congratulations! You received an offer!' : 'Offer Accepted!'}
                    </span>
                  </div>
                </div>
              )}

              {application.status === 'rejected' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <span className="text-lg">❌</span>
                    <span className="font-medium">Application was rejected. Keep applying!</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Application Details */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-secondary-900">Application Details</h3>
            </div>
            <div className="card-content">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-secondary-500 mb-2">Applied Date</h4>
                  <div className="flex items-center gap-2 text-sm text-secondary-900">
                    <HiOutlineCalendar className="h-4 w-4 text-primary-500" />
                    <span>{formatDate(application.appliedDate)}</span>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-secondary-500 mb-2">Source</h4>
                  <div className="flex items-center gap-2 text-sm text-secondary-900">
                    <HiOutlineBriefcase className="h-4 w-4 text-primary-500" />
                    <span>{sourceOptions.find(s => s.value === application.source)?.label || application.source}</span>
                  </div>
                </div>

                {application.applicationUrl && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500 mb-2">Job Posting</h4>
                    <div className="flex items-center gap-2 text-sm">
                      <HiOutlineGlobeAlt className="h-4 w-4 text-primary-500" />
                      <a 
                        href={application.applicationUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                      >
                        View Job Posting
                      </a>
                    </div>
                  </div>
                )}

                {application.resume && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500 mb-2">Resume Used</h4>
                    <div className="flex items-center gap-2 text-sm text-secondary-900">
                      <HiOutlineDocumentText className="h-4 w-4 text-primary-500" />
                      <span>{getResumeName(application.resume)}</span>
                    </div>
                  </div>
                )}

                {application.referralContact && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500 mb-2">Referral Contact</h4>
                    <div className="flex items-center gap-2 text-sm">
                      <HiOutlineUser className="h-4 w-4 text-primary-500" />
                      <button
                        onClick={() => {
                          const contactId = getContactId(application.referralContact);
                          if (contactId) {
                            navigate(`/contacts/${contactId}`);
                          }
                        }}
                        className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                      >
                        {getContactName(application.referralContact)}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Salary Information */}
          {application.salary && (application.salary.offered || application.salary.negotiated) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-secondary-900">💰 Salary Information</h3>
              </div>
              <div className="card-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {application.salary.offered && (
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500 mb-2">Offered Salary</h4>
                      <p className="text-lg font-semibold text-secondary-900">
                        {application.salary.offered.toLocaleString()} {application.salary.currency}
                        <span className="text-sm font-normal text-secondary-600 ml-1">
                          {application.salary.period}
                        </span>
                      </p>
                    </div>
                  )}
                  
                  {application.salary.negotiated && (
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500 mb-2">Negotiated Salary</h4>
                      <p className="text-lg font-semibold text-green-700">
                        {application.salary.negotiated.toLocaleString()} {application.salary.currency}
                        <span className="text-sm font-normal text-secondary-600 ml-1">
                          {application.salary.period}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notes and Cover Letter */}
          {(application.notes || application.coverLetter) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-secondary-900">Notes & Documents</h3>
              </div>
              <div className="card-content space-y-6">
                {application.notes && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500 mb-2">Preparation Notes</h4>
                    <p className="text-sm text-secondary-900 leading-relaxed whitespace-pre-wrap">
                      {application.notes}
                    </p>
                  </div>
                )}
                
                {application.coverLetter && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-500 mb-2">Cover Letter</h4>
                    <div className="bg-secondary-50 rounded-lg p-4">
                      <p className="text-sm text-secondary-900 leading-relaxed whitespace-pre-wrap">
                        {application.coverLetter}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Card */}
          {application.company && typeof application.company === 'object' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-secondary-900">Company</h3>
              </div>
              <div className="card-content">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-secondary-900">{application.company.name}</h4>
                    {application.company.location && (
                      <p className="text-sm text-secondary-500 mt-1">
                        <HiOutlineLocationMarker className="inline h-4 w-4 mr-1" />
                        {application.company.location}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/companies/${(application.company as Company)._id}`)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <HiOutlineEye className="h-4 w-4" />
                  </button>
                </div>
                
                {application.company.website && (
                  <a
                    href={application.company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Visit Company Website →
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Follow-up Information */}
          {application.followUpDate && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-secondary-900">Follow-up</h3>
              </div>
              <div className="card-content">
                <div className={`p-4 rounded-lg ${new Date(application.followUpDate) > new Date() ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <HiOutlineExclamationCircle className={`h-4 w-4 ${new Date(application.followUpDate) > new Date() ? 'text-amber-600' : 'text-gray-500'}`} />
                    <span className={`font-medium text-sm ${new Date(application.followUpDate) > new Date() ? 'text-amber-700' : 'text-gray-600'}`}>
                      {new Date(application.followUpDate) > new Date() ? 'Follow up on: ' : 'Followed up on: '}
                      {formatDate(application.followUpDate)}
                    </span>
                  </div>
                  {application.followUpNotes && (
                    <p className={`text-sm ${new Date(application.followUpDate) > new Date() ? 'text-amber-700' : 'text-gray-600'}`}>
                      {application.followUpNotes}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-secondary-900">Quick Actions</h3>
            </div>
            <div className="card-content space-y-3">
              <button
                onClick={handleEdit}
                className="w-full btn btn-secondary justify-center"
              >
                <HiOutlinePencil className="h-4 w-4 mr-2" />
                Edit Application
              </button>
              
              {application.company && typeof application.company === 'object' && (
                <button
                  onClick={() => navigate(`/companies/${(application.company as Company)._id}`)}
                  className="w-full btn btn-secondary justify-center"
                >
                  <HiOutlineEye className="h-4 w-4 mr-2" />
                  View Company
                </button>
              )}
              
              {application.referralContact && (
                <button
                  onClick={() => {
                    const contactId = getContactId(application.referralContact);
                    if (contactId) {
                      navigate(`/contacts/${contactId}`);
                    }
                  }}
                  className="w-full btn btn-secondary justify-center"
                >
                  <HiOutlineUser className="h-4 w-4 mr-2" />
                  View Contact
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Delete Application
            </h3>
            <p className="text-sm text-secondary-600 mb-6">
              Are you sure you want to delete this application for "{application.role}" at {getCompanyName(application.company)}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                Delete Application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetail; 